import * as React from 'react';
import classnames from 'classnames';
import isFunction from 'lodash/isFunction';

import Popover from 'components/popover/Popover';

import ContentGalleryContext from '../../context/ContentGalleryContext';
import ContentGalleryAsset from '../asset/ContentGalleryAsset';

import { getItemSize } from '../../utils/content-gallery';

import './content-gallery-item.scss';

import type {
  GalleryModalItem
} from 'types';
import type {
  ContentGalleryItem as ContentGalleryItemAlias
} from '../../types';

type Props = {
  item: ContentGalleryItemAlias,
  galleryItems: Array<GalleryModalItem>,
  galleryItemIndex: number
};

const ContentGalleryItem: React.FunctionComponent<Props> = ({
  item,
  galleryItems,
  galleryItemIndex
}: Props) => {
  const context = React.useContext(ContentGalleryContext);
  const renderTriggerOverlay = context?.renderTriggerOverlay;
  const renderTriggerPopover = context?.renderTriggerPopover;
  const renderTriggerFooter = context?.renderTriggerFooter;
  const itemSize = getItemSize(context?.itemSize);

  const containerClasses = classnames('content-gallery-item', {
    [`content-gallery-item--${itemSize}`]: Boolean(itemSize)
  });

  const renderOverlay = (): React.ReactNode => (
    isFunction(renderTriggerOverlay)
      ? renderTriggerOverlay(item)
      : null
  );

  const renderFooter = (): React.ReactNode => {
    if (!isFunction(renderTriggerFooter)) {
      return null;
    }

    return (
      <div className="content-gallery-item__footer">
        {renderTriggerFooter(item, galleryItemIndex)}
      </div>
    );
  };

  const renderAsset = (): React.ReactNode => (
    <React.Fragment>
      <div
        className="content-gallery-item__asset"
        style={{ width: itemSize, height: itemSize }}
      >
        <ContentGalleryAsset
          item={item}
          galleryItems={galleryItems}
          galleryItemIndex={galleryItemIndex}
        />
        {renderOverlay()}
      </div>
      {renderFooter()}
    </React.Fragment>
  );

  const renderPopover = (): React.ReactNode => (
    <Popover
      triggerComponent={renderAsset()}
      classNameTrigger="content-gallery-item__popover-trigger"
    >
      {
        isFunction(renderTriggerPopover)
          && renderTriggerPopover()
      }
    </Popover>
  );

  return (
    <div className={containerClasses}>
      {
        renderTriggerPopover
          ? renderPopover()
          : renderAsset()
      }
    </div>
  );
};

export default ContentGalleryItem;
